import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, BarChart3, History, Menu, X } from 'lucide-react';

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navigationItems = [
    {
      path: '/',
      icon: <Search className="w-4 h-4" />,
      label: 'AI SEO Analysis',
      description: 'Free website SEO checker'
    },
    {
      path: '/analysis',
      icon: <BarChart3 className="w-4 h-4" />,
      label: 'SEO Reports',
      description: 'View analysis results'
    },
    {
      path: '/history',
      icon: <History className="w-4 h-4" />,
      label: 'Analysis History',
      description: 'Track your progress'
    }
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Search className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900">oneclickAIseo</span>
                <span className="text-xs text-gray-500 -mt-1">AI-Powered SEO Tool</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors group relative ${
                  isActive(item.path)
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                title={item.description}
              >
                <div className="flex items-center space-x-2">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  {item.description}
                </div>
              </Link>
            ))}
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-blue-600 p-2 transition-colors touch-manipulation"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mobile-menu-enter absolute top-16 left-0 right-0 z-[60] bg-white border-t border-gray-200 shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Main Navigation */}
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`block px-3 py-3 rounded-md text-base font-medium transition-colors mobile-menu-item touch-manipulation ${
                    isActive(item.path)
                      ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {item.icon}
                    <div>
                      <span className="block">{item.label}</span>
                      <span className="text-sm text-gray-500">{item.description}</span>
                    </div>
                  </div>
                </Link>
              ))}
              
              {/* Mobile-specific quick actions */}
              <div className="pt-4 pb-3 border-t border-gray-200">
                <div className="px-3 py-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                    Quick Actions
                  </p>
                </div>
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors mobile-menu-item touch-manipulation"
                >
                  <div className="flex items-center space-x-2">
                    <Search className="w-4 h-4" />
                    <span>Start New AI SEO Analysis</span>
                  </div>
                </Link>
                <Link
                  to="/history"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors mobile-menu-item touch-manipulation"
                >
                  <div className="flex items-center space-x-2">
                    <History className="w-4 h-4" />
                    <span>View Analysis History</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu backdrop - positioned behind the menu with lower z-index */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-30 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}
    </header>
  );
};

export default Header;
